import {Component, computed, effect, inject, signal} from '@angular/core';
import {Title} from '../../../../components/title/title';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {StepperModule} from 'primeng/stepper';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {
  CreateCreditNoteRequest,
  CreditNoteLineDetails,
  CreditNoteService,
  InvoiceLineEdition,
  InvoiceService,
  UpdateCreditNoteRequest,
} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {CurrentUserService} from '../../../../services/current-user.service';
import {RouteUri} from '../../../../constants/route-uri';
import {scrollToFirstError} from '../../../../utils/form.utils';
import {CreditNoteStore} from '../../services/credit-note-store';
import {CreditNoteEdit} from '../credit-note-edit/credit-note-edit';
import {CreditNotePreview} from '../credit-note-preview/credit-note-preview';
import {CreditNoteSend} from '../credit-note-send/credit-note-send';

@Component({
  selector: 'elcia-credit-note-form',
  imports: [
    Title,
    TranslatePipe,
    ElciaButton,
    StepperModule,
    CreditNoteEdit,
    CreditNotePreview,
    CreditNoteSend,
  ],
  templateUrl: './credit-note-form.html',
  styleUrl: './credit-note-form.scss',
})
export class CreditNoteForm {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly invoiceService = inject(InvoiceService);
  private readonly creditNoteService = inject(CreditNoteService);
  private readonly creditNoteStore = inject(CreditNoteStore);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly currentUser = computed(() => this.currentUserService.currentUser());

  // Create-from-invoice mode: a source invoice id. Edit mode: an existing draft credit note id.
  private readonly sourceInvoiceId = this.route.snapshot.paramMap.has('invoiceId')
    ? Number(this.route.snapshot.paramMap.get('invoiceId'))
    : null;
  private readonly existingCreditNoteId = this.route.snapshot.paramMap.has('id')
    ? Number(this.route.snapshot.paramMap.get('id'))
    : null;

  // A credit note linked to an invoice must credit it in full: lines are prefilled and locked.
  protected readonly linkedToInvoice = signal<boolean>(this.sourceInvoiceId != null);

  protected readonly currentStep = signal<number>(history.state?.step ?? 1);

  protected readonly fg = new FormGroup<CreditNoteModelForm>({
    id: new FormControl(this.existingCreditNoteId),
    invoiceId: new FormControl(this.sourceInvoiceId),
    legalEntityId: new FormControl(this.currentUser()?.legalEntityId ?? null, Validators.required),
    customerId: new FormControl(null, Validators.required),
    creditNoteDate: new FormControl(new Date(), Validators.required),
    lines: new FormArray<FormGroup<CreditNoteLineModelForm>>([]),
  });

  protected readonly customerId = computed(() => this.customerIdSignal());
  private readonly customerIdSignal = signal<number | null>(null);

  // Source invoice the header and lines are prefilled from (create-from-invoice mode).
  private readonly invoiceQuery = injectQuery(() => ({
    queryKey: ['invoice', this.sourceInvoiceId],
    queryFn: () => lastValueFrom(this.invoiceService.getInvoiceById(this.sourceInvoiceId!)),
    enabled: this.sourceInvoiceId != null,
  }));

  // Existing draft credit note (edit mode).
  private readonly creditNoteQuery = injectQuery(() => ({
    queryKey: ['creditNote', this.existingCreditNoteId],
    queryFn: () => lastValueFrom(this.creditNoteService.getCreditNoteById(this.existingCreditNoteId!)),
    enabled: this.existingCreditNoteId != null,
  }));

  private invoicePopulated = false;
  private readonly populateFromInvoice = effect(() => {
    const invoice = this.invoiceQuery.data()?.invoice;
    if (!invoice || this.invoicePopulated) return;
    this.invoicePopulated = true;

    this.fg.patchValue({
      legalEntityId: invoice.legalEntityId,
      customerId: invoice.customerId,
      creditNoteDate: new Date(),
    });
    this.customerIdSignal.set(invoice.customerId);

    this.fg.controls.lines.clear();
    invoice.invoiceLines.forEach(line => this.fg.controls.lines.push(this.createLineFromInvoice(line)));
  });

  private creditNotePopulated = false;
  private readonly populateFromCreditNote = effect(() => {
    const creditNote = this.creditNoteQuery.data()?.creditNote;
    if (!creditNote || this.creditNotePopulated) return;
    this.creditNotePopulated = true;

    this.linkedToInvoice.set(creditNote.invoiceId != null);

    this.fg.patchValue({
      id: creditNote.id,
      invoiceId: creditNote.invoiceId ?? null,
      legalEntityId: creditNote.legalEntityId,
      customerId: creditNote.customerId,
      creditNoteDate: new Date(creditNote.date),
    });
    this.customerIdSignal.set(creditNote.customerId);

    this.fg.controls.lines.clear();
    creditNote.lines.forEach(line => this.fg.controls.lines.push(this.createLineFromCreditNote(line)));
  });

  private readonly onCurrentUserChange = effect(() => {
    const legalEntityId = this.currentUser()?.legalEntityId;
    if (legalEntityId && this.fg.controls.legalEntityId.value === null) {
      this.fg.controls.legalEntityId.setValue(legalEntityId);
    }
  });

  private readonly trackCustomerId = effect(() => {
    // Keep the customer id (used by the send step) in sync with manual header edits.
    const value = this.fg.controls.customerId.value;
    if (value != null) this.customerIdSignal.set(value);
  });

  protected goToPreview(): void {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      scrollToFirstError();
      return;
    }

    const id = this.fg.controls.id.value;
    if (id == null) {
      this.creditNoteStore.createMutation.mutate(this.buildCreateRequest(), {
        onSuccess: response => {
          this.fg.controls.id.setValue(response.id);
          this.router.navigate([RouteUri.CREDIT_NOTE_FORM, 'edit', response.id], {state: {step: 2}});
        },
      });
    } else {
      this.creditNoteStore.updateMutation.mutate({id, request: this.buildUpdateRequest()}, {
        onSuccess: () => this.currentStep.set(2),
      });
    }
  }

  protected goToStep(step: number): void {
    this.currentStep.set(step);
  }

  protected readonly isSaving = computed(() =>
    this.creditNoteStore.createMutation.isPending() || this.creditNoteStore.updateMutation.isPending(),
  );

  private buildCreateRequest(): CreateCreditNoteRequest {
    const value = this.fg.getRawValue();
    return {
      creditNoteDate: value.creditNoteDate!,
      customerId: value.customerId!,
      legalEntityId: value.legalEntityId!,
      invoiceId: value.invoiceId,
      lines: this.validLines().map(line => ({
        label: line.label!,
        description: line.description,
        quantity: line.quantity!,
        unitPrice: line.unitPrice ?? 0,
        productId: line.productId!,
        taxId: line.taxId!,
      })),
    };
  }

  private buildUpdateRequest(): UpdateCreditNoteRequest {
    const value = this.fg.getRawValue();
    return {
      creditNoteDate: value.creditNoteDate!,
      customerId: value.customerId!,
      legalEntityId: value.legalEntityId!,
      invoiceId: value.invoiceId,
      lines: this.validLines().map(line => ({
        id: line.id ?? undefined,
        label: line.label!,
        description: line.description,
        quantity: line.quantity!,
        unitPrice: line.unitPrice ?? 0,
        productId: line.productId!,
        taxId: line.taxId!,
      })),
    };
  }

  private validLines() {
    return this.fg.getRawValue().lines
      .filter(line => !!line.label && line.quantity != null && line.taxId != null && line.productId != null);
  }

  private createLineFromInvoice(line: InvoiceLineEdition) {
    return new FormGroup<CreditNoteLineModelForm>({
      id: new FormControl(null),
      productId: new FormControl<number | null>(line.productId ?? null, Validators.required),
      label: new FormControl(line.label ?? null, Validators.required),
      description: new FormControl(line.description ?? null),
      quantity: new FormControl(line.quantity ?? null, Validators.required),
      unitPrice: new FormControl(line.unitPrice ?? null, Validators.required),
      taxId: new FormControl(line.taxId ?? null, Validators.required),
    });
  }

  private createLineFromCreditNote(line: CreditNoteLineDetails) {
    return new FormGroup<CreditNoteLineModelForm>({
      id: new FormControl(line.id),
      productId: new FormControl<number | null>(line.productId ?? null, Validators.required),
      label: new FormControl(line.label ?? null, Validators.required),
      description: new FormControl(line.description ?? null),
      quantity: new FormControl(line.quantity ?? null, Validators.required),
      unitPrice: new FormControl(line.unitPrice ?? null, Validators.required),
      taxId: new FormControl(line.taxId ?? null, Validators.required),
    });
  }
}

export interface CreditNoteModelForm {
  id: FormControl<number | null>;
  invoiceId: FormControl<number | null>;
  legalEntityId: FormControl<number | null>;
  customerId: FormControl<number | null>;
  creditNoteDate: FormControl<Date | null>;
  lines: FormArray<FormGroup<CreditNoteLineModelForm>>;
}

export interface CreditNoteLineModelForm {
  id: FormControl<number | null>;
  productId: FormControl<number | null>;
  label: FormControl<string | null>;
  description: FormControl<string | null>;
  quantity: FormControl<number | null>;
  unitPrice: FormControl<number | null>;
  taxId: FormControl<number | null>;
}
